import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const KEY = "motowear_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [promoCode, setPromoCodeState] = useState("");
  const [giftCardCode, setGiftCardCodeState] = useState("");
  // Until the very first hydration from localStorage finishes we must NOT
  // persist `items` (which is still the initial empty array). Otherwise the
  // second effect races and overwrites the user's saved cart with [], wiping
  // the basket on every full page reload (e.g. when landing on /checkout).
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      try {
        setItems(JSON.parse(raw));
      } catch (err) {
        console.error("Failed to parse cart from storage:", err);
      }
    }
    const code = localStorage.getItem("motowear_promo");
    if (code) setPromoCodeState(code);
    const gc = localStorage.getItem("motowear_gift_card");
    if (gc) setGiftCardCodeState(gc);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const setPromoCode = (code) => {
    setPromoCodeState(code || "");
    if (code) localStorage.setItem("motowear_promo", code);
    else localStorage.removeItem("motowear_promo");
  };

  const setGiftCardCode = (code) => {
    setGiftCardCodeState(code || "");
    if (code) localStorage.setItem("motowear_gift_card", code);
    else localStorage.removeItem("motowear_gift_card");
  };

  // A variant is a unique combo of product + size + color
  // Personalisation makes each cart line unique — two keyrings with different
  // engravings must NOT merge. When personalisation is present we fingerprint it.
  const personalizationFp = (it) =>
    (it.personalization_name || it.personalization_text || it.personalization_image_url)
      ? `|${it.personalization_name || ""}|${it.personalization_text || ""}|${it.personalization_image_url || ""}`
      : "";

  const variantMatches = (a, product_id, size, color, fp = "", sizeFit = null) =>
    a.product_id === product_id
    && a.size === size
    && (a.color || null) === (color || null)
    && personalizationFp(a) === fp
    && (a.size_fit || null) === (sizeFit || null);

  const add = (product, size = "M", qty = 1, color = null, personalization = null, sizeFit = null) => {
    const norm = personalization ? {
      personalization_name: (personalization.name || "").trim() || null,
      personalization_text: (personalization.text || "").trim() || null,
      personalization_image_url: (personalization.image_url || "").trim() || null,
    } : {};
    const fp = personalizationFp(norm);
    setItems((prev) => {
      const idx = prev.findIndex((i) => variantMatches(i, product.id, size, color, fp, sizeFit));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, {
        product_id: product.id,
        title: product.title,
        image_url: product.image_url,
        unit: product.discount_price ?? product.price,
        size,
        color,
        size_fit: sizeFit || null,
        quantity: qty,
        ...norm,
      }];
    });
    setOpen(true);
    // GA4 ecommerce: add_to_cart event.
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "add_to_cart", {
          currency: "GBP",
          value: Number((product.discount_price ?? product.price) || 0) * qty,
          items: [{
            item_id: product.id,
            item_name: product.title,
            item_variant: [size, color].filter(Boolean).join(" / "),
            price: Number((product.discount_price ?? product.price) || 0),
            quantity: qty,
          }],
        });
      }
    } catch { /* analytics must never break UX */ }
  };

  const remove = (product_id, size, color = null, fp = "") =>
    setItems((prev) => prev.filter((i) => !variantMatches(i, product_id, size, color, fp)));

  const updateQty = (product_id, size, qty, color = null, fp = "") =>
    setItems((prev) => prev.map((i) => (variantMatches(i, product_id, size, color, fp) ? { ...i, quantity: Math.max(1, qty) } : i)));

  const clear = () => { setItems([]); setPromoCode(""); setGiftCardCode(""); };
  // Bulk-replace cart contents — used by Save Session recovery to hydrate
  // a previously-parked snapshot. Items should already be in the canonical
  // cart shape ({product_id, title, image, unit, quantity, size, ...}).
  const replaceItems = (next) => { setItems(Array.isArray(next) ? next : []); };

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.unit * i.quantity, 0);

  const value = useMemo(
    () => ({ items, open, setOpen, add, remove, updateQty, clear, replaceItems, count, subtotal, promoCode, setPromoCode, giftCardCode, setGiftCardCode }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, open, count, subtotal, promoCode, giftCardCode]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
